import { createContext, useContext, useState, useEffect } from "react";
import api from "../services/api";

const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  /* 🔥 PRICE HELPER (PRODUCT → SUBCATEGORY FALLBACK) */
  const getPrice = (product) => {
    if (!product) return 0;

    if (Number(product.discount_price) > 0)
      return Number(product.discount_price);

    if (Number(product.base_price) > 0) return Number(product.base_price);

    if (Number(product?.subcategory?.discount_price) > 0)
      return Number(product.subcategory.discount_price);

    return Number(product?.subcategory?.base_price) || 0;
  };

  /* 🔥 LOAD CART */
  const loadCart = async () => {
    const token = localStorage.getItem("token");

    const user = JSON.parse(localStorage.getItem("user"));

    if (!token || user?.role !== "customer") {
      setItems([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const res = await api.get("/cart");

      const data = res.data || [];

      const mapped = data.map((item) => {
        const product = item.product || {};

        return {
          cartId: item.id,

          variantId: item.id,

          productId: item.product_id,

          qty: Number(item.quantity) || 1,

          price: getPrice(product),

          name: product.name || "Product",
          model_name: item.model_name,

          image: product?.images?.[0]?.image_url || "/uploads/placeholder.jpg",

          product,

          attributes: product?.attributes || [],
        };
      });

      setItems(mapped);

      localStorage.setItem("cart", JSON.stringify(mapped));
    } catch (err) {
      console.error("Cart load failed:", err);

      const stored = JSON.parse(localStorage.getItem("cart")) || [];

      setItems(stored);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCart();
  }, []);

  /* ➕ ADD ITEM */
  const addItem = (product, variant = {}, qty = 1, cartId = null) => {
    const variantId = cartId || variant?.id || product.id;

    setItems((prev) => {
      const existing = prev.find((i) => i.variantId === variantId);

      if (existing) {
        return prev.map((i) =>
          i.variantId === variantId
            ? {
                ...i,
                qty: Number(i.qty) + Number(qty),
                cartId: existing.cartId || cartId,
              }
            : i,
        );
      }

      return [
        ...prev,
        {
          cartId,
          productId: product.id,
          variantId,
          name: product.name,

          price: getPrice(product), // ✅ FIX

          image: product?.images?.[0]?.image_url || "/uploads/placeholder.jpg",

          qty: Number(qty) || 1, // ✅ FIX NaN
        },
      ];
    });
  };

  const removeItem = (cartId) => {
    setItems((prev) => prev.filter((i) => Number(i.cartId) !== Number(cartId)));
  };

  /* 🔄 UPDATE */
  const updateQty = (cartId, qty) => {
    const safeQty = Number(qty);

    if (!safeQty || safeQty < 1) return;

    setItems((prev) =>
      prev.map((i) =>
        Number(i.cartId) === Number(cartId)
          ? {
              ...i,
              qty: safeQty,
            }
          : i,
      ),
    );
  };

  /* 🧹 CLEAR */
  const clearCart = () => setItems([]);

  /* 💰 TOTAL */
  const total = items.reduce(
    (sum, i) => sum + Number(i.price || 0) * Number(i.qty || 1),
    0,
  );

  /* 🔢 COUNT */
  const count = items.length;

  return (
    <CartContext.Provider
      value={{
        items,
        addItem,
        removeItem,
        updateQty,
        clearCart,
        total,
        count,
        loading,
        loadCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
